import React, { ReactNode, useEffect, useState } from 'react';
import './results-title.scss';

import t from '../../../labels/labelling-utils';
import {
  COLOR_SCHEMA_DARK,
  COLOR_SCHEMA_LIGHT,
  Color,
  ColorSchema,
  FontSize,
  FontWeight,
} from '../../../typings/design-tokens';
import { IComponent } from '../../../typings/generic-component';
import { isPercy } from '../../../utils/is-percy';
import { applyColorSchema, getMappedColor, makeClassNames } from '../../../utils/styles';
import { dateFormat } from '../../../utils/times';
import Text from '../text/text';

export const DEFAULT_UPDATE_INTERVAL = 30 * 1000; // 30 seconds

const DEFAULT_COLOR: Color = 'blue';
const COLOR_WITH_SCHEMA_DARK: Color = 'light';
const COLOR_WITH_SCHEMA_LIGHT: Color = 'blue';
const DATE_COLOR_WITH_SCHEMA_DARK: Color = 'light';
const DATE_COLOR_WITH_SCHEMA_LIGHT: Color = 'medium';

const getcolorSchemaMapping = () => {
  return {
    [COLOR_SCHEMA_DARK]: COLOR_WITH_SCHEMA_DARK,
    [COLOR_SCHEMA_LIGHT]: COLOR_WITH_SCHEMA_LIGHT,
  };
};

const getDateColorSchemaMapping = () => {
  return {
    [COLOR_SCHEMA_DARK]: DATE_COLOR_WITH_SCHEMA_DARK,
    [COLOR_SCHEMA_LIGHT]: DATE_COLOR_WITH_SCHEMA_LIGHT,
  };
};

export interface IResultsTitle extends IComponent {
  title: string;
  update?: ReactNode;
  selfUpdate?: boolean;
  updateInterval?: number;
  titleFontSize?: FontSize;
  titlefontWeight?: FontWeight;
  useUpdatePrefix?: boolean;
}

/**
 * Add Percy Mock component for ResultsTitle VRT
 */
const checkUpdate = (update: ReactNode, selfUpdate: boolean) => {
  if (isPercy()) {
    return update || dateFormat({ date: '04-02-1990', format: 'DD/MM, hh:mm' }); // MOCKED DATE for PERCY VRT
  } else {
    return selfUpdate ? dateFormat({ format: 'DD/MM, hh:mm' }) : update;
  }
};

const classNames = (colorSchema: ColorSchema) =>
  makeClassNames({
    resultTitle: ['results-title', `${applyColorSchema('results-title', colorSchema)}`],
    heading: 'results-title__heading',
    update: 'results-title__update',
  });

const ResultsTitle = ({
  title,
  update,
  /* istanbul ignore next: default value on mandatory field */ selfUpdate = false,
  /* istanbul ignore next: default value on mandatory field */ updateInterval = DEFAULT_UPDATE_INTERVAL,
  /* istanbul ignore next: default value on mandatory field */ colorSchema = 'light',
  /* istanbul ignore next: default value on mandatory field */ titleFontSize = '14',
  /* istanbul ignore next: default value on mandatory field */ titlefontWeight = '500',
  /* istanbul ignore next: default value on mandatory field */ useUpdatePrefix = true,
}: IResultsTitle) => {
  const [updateComponent, setComponent] = useState(checkUpdate(update, selfUpdate));
  const cn = classNames(colorSchema);
  const colorSchemaMapping = getcolorSchemaMapping();
  const dateColorSchemaMapping = getDateColorSchemaMapping();
  const color = getMappedColor({ colorSchemaMapping, colorSchema, defaultColor: DEFAULT_COLOR });
  const dateColor = getMappedColor({
    colorSchemaMapping: dateColorSchemaMapping,
    colorSchema,
  });

  /* Effect to update the component from props */
  useEffect(() => {
    if (!selfUpdate) {
      setComponent(update);
    }
  }, [update, selfUpdate]);

  /** update Label team each updateIntervals */
  useEffect(() => {
    let timer: NodeJS.Timeout;
    /* istanbul ignore else */ if (selfUpdate) {
      timer = setInterval(() => {
        setComponent(dateFormat({ format: 'DD/MM, hh:mm' }));
      }, updateInterval);
    }
    return () => {
      clearInterval(timer);
    };
  }, [selfUpdate, updateInterval]);

  return (
    <div className={cn.resultTitle}>
      <Text className={cn.heading} tag="h2" color={color} fontSize={titleFontSize} fontWeight={titlefontWeight}>
        {title}
      </Text>
      {updateComponent && (
        <Text className={cn.update} fontSize="12" color={dateColor}>
          {useUpdatePrefix
            ? `${t('results.title.updated')}: ${isPercy() ? ' percy-mocked-time' : updateComponent}`
            : updateComponent}
        </Text>
      )}
    </div>
  );
};

export default ResultsTitle;
